import { useMemo } from 'react';
import { InstructionType, RedistributionImportInputData } from './redistributionImportTypes';
import { useSelector } from 'react-redux';
import { ReduxState } from '../../../../../store';
import { EntityIdentificationType } from '@ydistri/api-sdk';
import { ImportFormatDataType, ImportFormatItem } from '../../../../../lib/imports/importTypes';

/**
 * Returns a list of keys that form the format of importContent. The content may changed based on user's choice of instruction.
 */
const useRedistributionImportFormat = (): ImportFormatItem<RedistributionImportInputData>[] => {
  const instructionSpecification = useSelector(
    (state: ReduxState) => state.calculations.redistributionImport.instructionSpecification,
  );

  const identificationData = useSelector(
    (state: ReduxState) => state.calculations.redistributionImport.identificationData,
  );

  return useMemo((): ImportFormatItem<RedistributionImportInputData>[] => {
    const productIdentifier =
      identificationData.find(identification => identification.type === 'Product')?.value ??
      EntityIdentificationType.CustomerId;
    const storeIdentifier =
      identificationData.find(identification => identification.type === 'Store')?.value ??
      EntityIdentificationType.CustomerId;

    const result: ImportFormatItem<RedistributionImportInputData>[] = [
      {
        id: 'productIdentifier',
        label: productIdentifier === EntityIdentificationType.Code ? 'Product code' : 'Product ID',
        dataType: ImportFormatDataType.STRING,
      },
      {
        id: 'sourceStoreIdentifier',
        label:
          storeIdentifier === EntityIdentificationType.Code
            ? 'Source Store Code'
            : 'Source Store ID',
        dataType: ImportFormatDataType.STRING,
      },
      {
        id: 'targetStoreIdentifier',
        label:
          storeIdentifier === EntityIdentificationType.Code
            ? 'Target Store Code'
            : 'Target Store ID',
        dataType: ImportFormatDataType.STRING,
      },
    ];

    if (
      instructionSpecification === InstructionType.QUANTITY ||
      instructionSpecification === InstructionType.QUANTITY_DESCRIPTION
    ) {
      result.push({
        id: 'count',
        label: 'Quantity',
        dataType: ImportFormatDataType.NUMBER,
      });
    }

    if (
      instructionSpecification === InstructionType.DESCRIPTION ||
      instructionSpecification === InstructionType.QUANTITY_DESCRIPTION
    ) {
      result.push({
        id: 'description',
        label: 'Description',
        dataType: ImportFormatDataType.STRING,
      });
    }

    return result;
  }, [identificationData, instructionSpecification]);
};

export default useRedistributionImportFormat;
